import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/Warehouse";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import FulfillmentWorkQueueApi from "../../../../../infrastructure/api/fulfillmentWorkQueue";
import { downloadPdfBlob } from "../../contractor-portal/utils/contractPdfActions";

const OPERATIONS_SOURCE = "SolarentOperator";

const pick = (raw, camel) =>
  raw?.[camel] ?? raw?.[camel.charAt(0).toUpperCase() + camel.slice(1)] ?? null;

const dash = (value) => {
  if (value == null || value === "") return "—";
  return String(value);
};

const join = (...parts) => parts.filter((p) => p != null && String(p).trim() !== "").join(" — ") || "—";

const mapsLink = (x, y) => {
  const lat = Number(x);
  const lng = Number(y);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) return "";
  return `https://www.google.com/maps?q=${lat},${lng}`;
};

const unwrapBlob = async (response) => {
  const blob = response?.data ?? response;
  if (blob && blob.type && blob.type.includes("application/json")) {
    const text = await blob.text();
    let msg = "خطا در تولید PDF";
    try {
      const json = JSON.parse(text);
      msg = json.message || json.Message || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return blob;
};

const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      {children}
    </Typography>
  </Box>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const FulfillmentDispatchDialog = ({ open, requestId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [downloading, setDownloading] = useState("");

  const load = useCallback(async () => {
    if (!open || !requestId) return;
    setLoading(true);
    try {
      const res = await FulfillmentWorkQueueApi.getProjectDetail(OPERATIONS_SOURCE, requestId);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در دریافت اطلاعات پروژه");
        setDetail(null);
        return;
      }
      setDetail(res?.data ?? res);
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت اطلاعات پروژه");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [open, requestId]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadDoc = async (kind) => {
    setDownloading(kind);
    try {
      const call =
        kind === "exit"
          ? FulfillmentWorkQueueApi.downloadWarehouseExitPdf
          : FulfillmentWorkQueueApi.downloadDeliveryNotePdf;
      const blob = await unwrapBlob(await call(OPERATIONS_SOURCE, requestId));
      const tracking = pick(detail, "queue")
        ? pick(pick(detail, "queue"), "requestTrackingCode")
        : requestId;
      const fileName =
        kind === "exit"
          ? `warehouse-exit-${tracking}.pdf`
          : `delivery-note-${tracking}.pdf`;
      downloadPdfBlob(blob, fileName);
    } catch (e) {
      toast.error(e?.message || "دانلود PDF ناموفق بود.");
    } finally {
      setDownloading("");
    }
  };

  const queue = pick(detail, "queue") || {};
  const items = pick(detail, "equipmentItems") || [];
  const xPoint = pick(detail, "xPoint");
  const yPoint = pick(detail, "yPoint");
  const maps = mapsLink(xPoint, yPoint);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>اطلاعات ارسال تجهیزات</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !detail ? (
          <Alert severity="warning">اطلاعات این پروژه در دسترس نیست.</Alert>
        ) : (
          <Stack spacing={2}>
            <Alert severity="info">
              پرونده را باز نکنید؛ انبار با همین اطلاعات و دو برگه PDF ارسال را انجام می‌دهد.
            </Alert>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <Field label="کد پیگیری">{dash(pick(queue, "requestTrackingCode"))}</Field>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="شماره قرارداد">{dash(pick(detail, "contractNumber"))}</Field>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="تاریخ قرارداد">{dash(pick(detail, "contractDateFa"))}</Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="متقاضی">
                  {join(pick(detail, "applicantType"), pick(detail, "applicantFullName"))}
                </Field>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Field label="کد ملی / شناسه">{dash(pick(detail, "applicantNationalId"))}</Field>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Field label="موبایل">{dash(pick(detail, "applicantMobile"))}</Field>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="نوع درخواست">{dash(pick(detail, "financingTitle"))}</Field>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="نوع نیروگاه">
                  {join(pick(detail, "powerPlantTypeTitle"), pick(detail, "systemType"))}
                </Field>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="ظرفیت">{dash(pick(detail, "capacityTitle"))}</Field>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="کاربری محل">{dash(pick(detail, "propertyType"))}</Field>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="محل نصب">{dash(pick(detail, "installationLocationType"))}</Field>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="شماره اشتراک">{dash(pick(detail, "electricitySubscriptionNo"))}</Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="پیمانکار اجرا">{dash(pick(detail, "executorContractorName"))}</Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="موبایل پیمانکار">{dash(pick(detail, "executorContractorMobile"))}</Field>
              </Grid>
              <Grid item xs={12}>
                <Field label="آدرس محل نصب">
                  {join(pick(detail, "provinceTitle"), pick(detail, "cityTitle"), pick(detail, "fullAddress"))}
                </Field>
              </Grid>
              <Grid item xs={12}>
                <Field label="لوکیشن">
                  {maps ? (
                    <Link href={maps} target="_blank" rel="noopener noreferrer">
                      باز کردن گوگل‌مپ
                    </Link>
                  ) : (
                    "—"
                  )}
                </Field>
              </Grid>
            </Grid>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                اقلام تجهیزات طبق قرارداد امضاشده
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ردیف</TableCell>
                    <TableCell>نوع</TableCell>
                    <TableCell>برند / مدل</TableCell>
                    <TableCell>مقدار</TableCell>
                    <TableCell>توضیح</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>اقلام تجهیزات در قرارداد ثبت نشده است.</TableCell>
                    </TableRow>
                  ) : (
                    items.map((item, index) => (
                      <TableRow key={pick(item, "rowNo") ?? index}>
                        <TableCell>{dash(pick(item, "rowNo"))}</TableCell>
                        <TableCell>{dash(pick(item, "equipmentType"))}</TableCell>
                        <TableCell>{dash(pick(item, "brandModel"))}</TableCell>
                        <TableCell>{dash(pick(item, "quantity"))}</TableCell>
                        <TableCell>{dash(pick(item, "description"))}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 3, py: 2 }}>
        <Button
          variant="contained"
          startIcon={
            downloading === "exit" ? <CircularProgress size={16} color="inherit" /> : <WarehouseOutlinedIcon />
          }
          disabled={!detail || Boolean(downloading)}
          onClick={() => downloadDoc("exit")}
        >
          دانلود برگه خروج انبار
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={
            downloading === "delivery" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <LocalShippingOutlinedIcon />
            )
          }
          disabled={!detail || Boolean(downloading)}
          onClick={() => downloadDoc("delivery")}
        >
          دانلود برگه تحویل متقاضی
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} startIcon={<DescriptionOutlinedIcon />} disabled={Boolean(downloading)}>
          بستن
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FulfillmentDispatchDialog.propTypes = {
  open: PropTypes.bool,
  requestId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};

export default FulfillmentDispatchDialog;
